// src/store/store.js
import { configureStore, combineReducers } from "@reduxjs/toolkit"
import {
  persistStore, persistReducer, createMigrate,
  FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER,
} from "redux-persist"
import storage from "redux-persist/lib/storage"

import userReducer  from "../features/userSlice"
import tabsReducer  from "../features/tabsSlice"
import formsReducer from "../features/formsSlice"
import uiReducer    from "../features/uiSlice"

/* ── Why persistence is switched off here ───────────────────────────
   The recurring blank page was traced to the persisted `ui` slice. It
   carries server-loaded / transient fields (screenPrefs, screenPrefsLoading,
   screenPrefsLoadedAt, syncStatus, syncError). When an OLD-shaped `ui`
   blob rehydrated and loadUserPrefs' reducers then ran against it, a
   field read threw INSIDE a reducer/selector - which happens outside React
   render, so the error boundaries never got a chance and the page went blank.

   The three persisted slices are all safe to start clean:
     • tabs / forms - were empty anyway; correct to reset on reload.
     • ui           - loadUserPrefs() re-fetches theme/font/density from the
                      server on mount, so nothing is actually lost.

   So we persist NOTHING. The store is plain, deterministic, and can never
   rehydrate a stale shape again. `user` was never persisted (auth lives
   elsewhere), so no one is logged out.
   ─────────────────────────────────────────────────────────────────── */

/* One-time cleanup: remove every persist blob this app ever wrote, so old
   clients don't keep a poisoned `ui` blob sitting in localStorage. Safe to
   delete this block after ~1 month once all clients have loaded once. */
try {
  Object.keys(window.localStorage)
    .filter((k) => k.startsWith("persist:saymyerp"))
    .forEach((k) => window.localStorage.removeItem(k))
} catch (e) { /* private mode / storage disabled - ignore */ }

const rootReducer = combineReducers({
  user:  userReducer,
  tabs:  tabsReducer,
  forms: formsReducer,
  ui:    uiReducer,
})

const persistConfig = {
  key:       "saymyerp-v1",
  storage,
  whitelist: [],              // ← persist nothing (was ["tabs","forms","ui"])
  blacklist: ["user"],
  version:   4,
  migrate:   createMigrate({ 4: () => ({}) }, { debug: false }),
}

export const store = configureStore({
  reducer: persistReducer(persistConfig, rootReducer),
  middleware: (gDM) => gDM({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    },
  }),
})

export const persistor = persistStore(store)
